/* screens/gift/GiftBuy.jsx — «Подарить Ресториум» (/gift).
   Вход из меню профиля в сайдбаре (UserMenu).

   Три шага, а не одна длинная форма. Человек здесь решает три РАЗНЫХ вопроса —
   что дарю, кому и как оплачиваю, — и смешивать их в одну простыню значит
   показать поля «дата отправки письма» раньше, чем он выбрал подарок. Шаги
   пронумерованы явно (правило «Progress Indicators»): без счётчика человек не
   знает, сколько ещё осталось, и бросает на середине платной формы.

   Второй шаг ЦЕЛИКОМ необязателен — таково продуктовое решение: даритель вправе
   просто оплатить и скачать PDF, чтобы вручить лично. Поэтому кнопка шага
   называется «Дальше», а не «Заполните получателя», и ни одно поле не помечено
   обязательным. Единственная связка — запланировать отправку без адреса нельзя,
   и об этом сказано прямо у поля, а не в тосте после нажатия.

   Деньги: сумма приходит строкой DRF (price_rub), считаем через Number.
   Оплата — карта (Робокасса) и счёт на юрлицо (Точка), обе за своими флагами. */

import React from 'react';
import { useAuth } from '../../app/providers/AuthProvider.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { InvoicePayModal } from '../../components/patterns/InvoicePayModal.jsx';
import { PageHeader } from '../../components/patterns/PageHeader.jsx';
import { QueryBoundary } from '../../components/patterns/States.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Field, Segmented, TextInput, Textarea } from '../../components/ui/Field.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import * as giftsApi from '../../lib/api/gifts.js';
import { fmt } from '../../lib/format.js';
import { RR } from '../../lib/router.jsx';

const { useState, useEffect, useMemo, useCallback } = React;
const { useNavigate } = RR;

const AUDIENCES = [
  { value: 'personal', label: 'Личный ассистент' },
  { value: 'business', label: 'Для заведения' },
];

const money = (v) => (v == null || v === '' ? '—' : fmt.money(Number(v)));

/** «30 дн» → «1 месяц». Человек покупает месяцы, а не дни. */
function periodLabel(days) {
  if (days % 30 === 0 && days >= 30) {
    const m = days / 30;
    return `${m} ${fmt.plural(m, 'месяц', 'месяца', 'месяцев')}`;
  }
  return `${days} ${fmt.plural(days, 'день', 'дня', 'дней')}`;
}

/** `2026-08-28T10:00` → «28.08.2026, 10:00 МСК».

    Строку показываем как есть, подписав пояс, и НЕ прогоняем через Date():
    она уже московская и без пояса, а браузер прочитал бы её в своём — клиент
    из Владивостока увидел бы в итогах другое время, чем то, что сам выбрал. */
function humanSchedule(local) {
  if (!local) return '';
  const [date, time] = local.split('T');
  if (!time) return local;
  const [y, m, d] = date.split('-');
  return `${d}.${m}.${y}, ${time} МСК`;
}

/** Локальное «сейчас + сутки» в формате datetime-local (МСК-строка без пояса).
    Сервер трактует наивную строку как московскую — см. gifts.parse_msk. */
function defaultSchedule() {
  const d = new Date(Date.now() + 24 * 3600 * 1000);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T10:00`;
}

function Step({ n, active, done, title }) {
  return (
    <div className={'gift-step' + (active ? ' is-active' : '') + (done ? ' is-done' : '')}>
      <span className="gift-step__num" aria-hidden="true">
        {done ? <Icon name="check" size={13} /> : n}
      </span>
      <span className="gift-step__title">{title}</span>
    </div>
  );
}

/* Тариф и его варианты по сроку — ОДНА карточка.

   Слаг вида `<base>_<N>m` («biz_s_6m») — не отдельный товар, а тот же тариф на
   другой срок; так это устроено и в модели, и на основном экране тарифов.
   Показать их плоским списком значило бы выложить в бизнес-линейке двенадцать
   карточек, где половина — один и тот же «Бизнес S». Правило разбора слага
   повторяет SubscriptionTab: разъехавшись, витрины назвали бы одно и то же
   разными вещами. */
function PlanFamilyCard({ family, selectedSlug, onSelect }) {
  const active = family.options.find((o) => o.plan.slug === selectedSlug);
  const shown = active || family.options[0];
  const selected = !!active;
  return (
    <div className={'gift-plan' + (selected ? ' is-selected' : '')}>
      <button type="button" className="gift-plan__main"
        aria-pressed={selected}
        onClick={() => onSelect(shown.plan.slug)}>
        <span className="gift-plan__head">
          <span className="gift-plan__name">{family.name}</span>
          {selected && <Icon name="check" size={16} className="gift-plan__check" />}
        </span>
        <span className="gift-plan__price num">{money(shown.plan.price_rub)}</span>
        <span className="gift-plan__period t-caption text-mute">
          за {periodLabel(shown.plan.period_days)}
        </span>
      </button>
      {family.options.length > 1 && (
        <div className="gift-periods" role="group"
          aria-label={`Срок для тарифа ${family.name}`}>
          {family.options.map((o) => (
            <button key={o.plan.slug} type="button"
              className={'gift-period' + (shown.plan.slug === o.plan.slug ? ' is-active' : '')}
              aria-pressed={shown.plan.slug === o.plan.slug}
              onClick={() => onSelect(o.plan.slug)}>
              {o.months} мес.
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Тарифы → семейства {name, options:[{plan, months}]}, отсортированные по цене. */
function buildFamilies(plans) {
  const byBase = new Map();
  plans.forEach((p) => {
    const m = /^(.+)_(\d+)m$/.exec(p.slug);
    const base = m ? m[1] : p.slug;
    const months = m ? Number(m[2]) : Math.max(1, Math.round(p.period_days / 30));
    if (!byBase.has(base)) byBase.set(base, { base, name: p.name, options: [] });
    byBase.get(base).options.push({ plan: p, months });
  });
  const families = [];
  byBase.forEach((f) => {
    f.options.sort((a, b) => a.months - b.months);
    // Имя семейства берём у САМОГО КОРОТКОГО варианта: у длинных в названии
    // может остаться приписка срока, и «Бизнес S (12 мес.)» стало бы заголовком
    // карточки, внутри которой выбирают месяц.
    f.name = f.options[0].plan.name;
    families.push(f);
  });
  families.sort((a, b) =>
    Number(a.options[0].plan.price_rub) - Number(b.options[0].plan.price_rub));
  return families;
}

function GiftBuy() {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();

  const [phase, setPhase] = useState('loading');   // loading | ready | error
  const [plans, setPlans] = useState([]);
  const [ttlDays, setTtlDays] = useState(365);

  const [step, setStep] = useState(1);
  const [audience, setAudience] = useState('personal');
  const [slug, setSlug] = useState('');

  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [senderName, setSenderName] = useState('');
  const [message, setMessage] = useState('');
  const [wantsSchedule, setWantsSchedule] = useState(false);
  const [scheduledFor, setScheduledFor] = useState(defaultSchedule());

  const [errors, setErrors] = useState({});
  const [topError, setTopError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      const r = await giftsApi.listGiftPlans();
      if (!alive) return;
      if (!r.ok) { setPhase('error'); return; }
      setPlans((r.data && r.data.plans) || []);
      setTtlDays((r.data && r.data.ttl_days) || 365);
      setPhase('ready');
    })();
    return () => { alive = false; };
  }, []);

  // Подпись «от кого» по умолчанию — имя дарителя. Ставим ОДИН раз и только в
  // пустое поле: перетирать то, что человек уже вписал, нельзя.
  useEffect(() => {
    if (!senderName && user && user.full_name) setSenderName(user.full_name);
  }, [user]);   // eslint-disable-line react-hooks/exhaustive-deps

  const visible = useMemo(
    () => buildFamilies(plans.filter((p) => p.audience === audience)),
    [plans, audience],
  );
  const plan = useMemo(
    () => plans.find((p) => p.slug === slug) || null,
    [plans, slug],
  );

  // Смена аудитории сбрасывает выбор: тариф заведения, оставшийся выбранным на
  // личной вкладке, оплатился бы молча не тем, что видит человек.
  const changeAudience = (next) => {
    setAudience(next);
    setSlug('');
  };

  const validateGift = () => {
    const errs = {};
    const email = recipientEmail.trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errs.email = 'Проверьте адрес: похоже, в нём опечатка.';
    }
    if (wantsSchedule && !email) {
      errs.email = 'Чтобы отправить письмо по расписанию, нужен адрес получателя.';
    }
    if (wantsSchedule && scheduledFor && new Date(scheduledFor) <= new Date()) {
      errs.schedule = 'Выберите будущее время.';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const giftBody = useCallback(() => ({
    plan_slug: slug,
    recipient_name: recipientName.trim(),
    recipient_email: recipientEmail.trim(),
    sender_name: senderName.trim(),
    message: message.trim(),
    scheduled_for: wantsSchedule ? scheduledFor : '',
  }), [slug, recipientName, recipientEmail, senderName, message, wantsSchedule, scheduledFor]);

  const payByCard = async () => {
    setBusy(true);
    setTopError(null);
    const r = await giftsApi.startGiftCheckout(giftBody());
    setBusy(false);
    if (!r.ok) {
      setTopError((r.data && r.data.detail) || 'Не удалось начать оплату.');
      return;
    }
    const redirect = r.data && r.data.redirect_url;
    const invoiceId = r.data && r.data.invoice_id;
    // Мок-шлюз (dev) — это экран самого приложения: уходим внутренним роутом,
    // а не абсолютным redirect_url, который ведёт на боевой фронт.
    if (redirect && /\/checkout\/mock\//.test(redirect)) {
      navigate('/checkout/mock/' + encodeURIComponent(invoiceId));
      return;
    }
    if (redirect) { window.location.href = redirect; return; }
    toast.show('Не удалось начать оплату', 'danger');
  };

  const canPay = !!plan;
  const showInvoice = !!(user && user.tochka_invoicing_enabled);

  return (
    /* Канон раздела: `.section-page` + `PageHeader` + `.section-body`.
       Свой контейнер здесь заводить нельзя — именно `.section-body` держит
       общие `--page-pad`/`--page-max`, а `PageHeader` даёт липкую шапку с
       гамбургером (на телефоне это ЕДИНСТВЕННЫЙ вход в навигацию), колокольчик
       и переключатель пространства. */
    <div className="section-page gift-section">
      <PageHeader
        title="Подарить Ресториум"
        subtitle="Подарок" />
      <div className="section-body gift-page">
        <p className="t-body text-dim gift-lead">
          Оплатите тариф — получатель активирует его кодом из сертификата.
        </p>

      <QueryBoundary
        loading={phase === 'loading'}
        error={phase === 'error' ? new Error('failed') : null}
        onRetry={() => window.location.reload()}
        skeleton={<div className="stack stack-16">
          <span className="skeleton" style={{ height: 120, borderRadius: 'var(--radius-lg)' }} />
          <span className="skeleton" style={{ height: 220, borderRadius: 'var(--radius-lg)' }} />
        </div>}>

        <nav className="gift-steps" aria-label="Шаги оформления">
          <Step n={1} title="Подарок" active={step === 1} done={step > 1} />
          <Step n={2} title="Сертификат" active={step === 2} done={step > 2} />
          <Step n={3} title="Оплата" active={step === 3} done={false} />
        </nav>

        {topError && (
          <div className="callout callout--bad" role="alert">
            <Icon name="alert-circle" size={16} />
            <span>{topError}</span>
          </div>
        )}

        {/* ── Шаг 1: что дарим ─────────────────────────────────────────── */}
        {step === 1 && (
          <section className="gift-card stack stack-16">
            <div className="stack stack-8">
              <h2 className="t-h4">Что дарим</h2>
              <Segmented options={AUDIENCES} value={audience} onChange={changeAudience}
                ariaLabel="Кому предназначен тариф" />
              <p className="t-caption text-mute">
                {audience === 'personal'
                  ? 'Ассистент, техкарты и калькуляция себестоимости — для одного человека.'
                  : 'Всё то же плюс аналитика кассы и командный доступ — для заведения.'}
              </p>
            </div>

            <div className="gift-plans">
              {visible.map((f) => (
                <PlanFamilyCard key={f.base} family={f} selectedSlug={slug} onSelect={setSlug} />
              ))}
            </div>

            {visible.length === 0 && (
              <p className="t-body text-mute">Тарифов этой линейки сейчас нет.</p>
            )}

            <div className="gift-actions">
              <Button variant="primary" size="lg" disabled={!plan}
                onClick={() => setStep(2)}>
                Дальше
              </Button>
              <Button variant="ghost" size="lg" onClick={() => navigate('/gift/my')}>
                Мои подарки
              </Button>
            </div>
          </section>
        )}

        {/* ── Шаг 2: сертификат ────────────────────────────────────────── */}
        {step === 2 && (
          <section className="gift-card stack stack-16">
            <div className="stack stack-4">
              <h2 className="t-h4">Сертификат</h2>
              <p className="t-caption text-mute">
                Всё необязательно. Можно ничего не заполнять — тогда просто
                скачаете PDF и вручите его сами.
              </p>
            </div>

            <div className="gift-grid">
              <Field label="Имя получателя" htmlFor="gift-rname">
                <TextInput id="gift-rname" value={recipientName} placeholder="Мария"
                  onChange={(e) => setRecipientName(e.target.value)} />
              </Field>
              <Field label="От кого" htmlFor="gift-sname">
                <TextInput id="gift-sname" value={senderName} placeholder="Ваше имя"
                  onChange={(e) => setSenderName(e.target.value)} />
              </Field>
            </div>

            <Field label="Пожелание" htmlFor="gift-msg"
              hint="Появится на сертификате и в письме">
              <Textarea id="gift-msg" rows={3} value={message} maxLength={300}
                placeholder="С днём рождения! Пусть считается само."
                onChange={(e) => setMessage(e.target.value)} />
            </Field>

            <Field label="Почта получателя" htmlFor="gift-email" error={errors.email}
              hint={!errors.email ? 'Нужна только если хотите, чтобы мы отправили письмо' : undefined}>
              <TextInput id="gift-email" type="email" value={recipientEmail}
                invalid={errors.email ? true : undefined}
                placeholder="friend@example.com"
                onBlur={validateGift}
                onChange={(e) => setRecipientEmail(e.target.value)} />
            </Field>

            <div className="gift-schedule">
              <label className="gift-check">
                <input type="checkbox" className="checkbox__input" checked={wantsSchedule}
                  onChange={(e) => setWantsSchedule(e.target.checked)} />
                <span className="checkbox__box" aria-hidden="true"><Icon name="check" size={12} /></span>
                <span>Отправить письмо в назначенное время</span>
              </label>
              {wantsSchedule && (
                <Field label="Когда отправить (МСК)" htmlFor="gift-when" error={errors.schedule}
                  hint={!errors.schedule ? 'Дату можно менять, пока письмо не ушло' : undefined}>
                  <TextInput id="gift-when" type="datetime-local" value={scheduledFor}
                    invalid={errors.schedule ? true : undefined}
                    onChange={(e) => setScheduledFor(e.target.value)} />
                </Field>
              )}
            </div>

            <div className="gift-actions">
              <Button variant="primary" size="lg"
                onClick={() => { if (validateGift()) setStep(3); }}>
                Дальше
              </Button>
              <Button variant="ghost" size="lg" onClick={() => setStep(1)}>Назад</Button>
            </div>
          </section>
        )}

        {/* ── Шаг 3: оплата ────────────────────────────────────────────── */}
        {step === 3 && plan && (
          <section className="gift-card stack stack-16">
            <h2 className="t-h4">Проверьте и оплатите</h2>

            <dl className="gift-summary">
              <div><dt>Тариф</dt><dd>{plan.name}</dd></div>
              <div><dt>Срок</dt><dd>{periodLabel(plan.period_days)}</dd></div>
              <div><dt>Кому</dt><dd>{recipientName.trim() || '—'}</dd></div>
              <div><dt>Письмо</dt><dd>
                {recipientEmail.trim()
                  ? (wantsSchedule
                      ? `${recipientEmail.trim()}, ${humanSchedule(scheduledFor)}`
                      : `${recipientEmail.trim()}, сразу после оплаты`)
                  : 'Не отправляем — скачаете PDF'}
              </dd></div>
              <div className="gift-summary__total">
                <dt>К оплате</dt><dd className="num">{money(plan.price_rub)}</dd>
              </div>
            </dl>

            <div className="callout callout--info">
              <Icon name="info" size={16} />
              <span>
                Разовый платёж: карта не привязывается, автосписаний не будет.
                Код действует {ttlDays} дней с момента покупки.
              </span>
            </div>

            <div className="gift-actions">
              <Button variant="primary" size="lg" loading={busy} disabled={!canPay}
                iconLeft="card" onClick={payByCard}>
                Оплатить картой
              </Button>
              {showInvoice && (
                <Button variant="secondary" size="lg" iconLeft="file"
                  onClick={() => setInvoiceOpen(true)}>
                  Счёт на юрлицо
                </Button>
              )}
              <Button variant="ghost" size="lg" onClick={() => setStep(2)}>Назад</Button>
            </div>
          </section>
        )}
      </QueryBoundary>

      </div>

      {showInvoice && (
        <InvoicePayModal
          open={invoiceOpen}
          onClose={() => setInvoiceOpen(false)}
          planLabel={plan ? plan.name : ''}
          amountRub={plan ? plan.price_rub : null}
          /* Счёт за подарок выставляет свой эндпоинт: сумма берётся из ЦЕНЫ
             ТАРИФА, а не из подписки пространства. Общий invoice-путь посчитал
             бы пропорцию к текущей подписке дарителя и превратил подарок в
             скидку размером с остаток его собственного тарифа. */
          submitInvoice={async (requisites) => {
            const r = await giftsApi.startGiftInvoice({
              ...giftBody(), requisites, offer_accepted: true,
            });
            if (!r.ok) throw (r.data || { detail: 'Не удалось выставить счёт.' });
            return r.data;
          }}
          onCreated={(invoiceId) => {
            setInvoiceOpen(false);
            navigate('/checkout/invoice/' + encodeURIComponent(invoiceId));
          }} />
      )}
    </div>
  );
}

export { GiftBuy };
